import { useEffect, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import { useRedirectAndNotify } from '@eneco/components-next/Notification/Notification';
import {
  CompanyFormatTypeDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
  putEnecoBeXapiSiteApiV1AccountsByAccountNumberCompanyDetails,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { unwrapValidationError } from '@eneco/dc-be/utils/unwrapError.js';
import { typedYupResolver } from '@eneco/hooks/form';
import { Bucket, Button, Checkbox, Form } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { CompanyDataRendering } from '@sitecore/types/CompanyData';
import { useRouter } from 'next/router';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import * as yup from 'yup';

import { CompanyNumberInput, isValidCompanyNumber } from '../../../../../components/CompanyNumberInput';
import DataErrorNotification from '../../../../../components/DataErrorNotification';
import ReadFormField from '../../../../../components/ReadFormField';
import { useSetNotificationAndScrollToTop } from '../../../../../hooks/useSetNotificationAndScrollToTop';

const EditCompanyData = () => {
  const { push } = useRouter();

  const { fields } = useContent<CompanyDataRendering>();

  const { selectedAccount, isCurrentAccountReader } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { trackEventBE } = useTracking();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const setNotification = useSetNotificationAndScrollToTop();
  const redirectAndNotify = useRedirectAndNotify();
  const { mutate } = useSWRConfig();
  const { language } = useApplication();

  const editCompanyDataSchema = yup.object({
    showCompanyNumber: yup.boolean(),
    name: yup
      .string()
      .required(fields.companyNameFormField?.value.requiredMessage)
      .max(100, fields.companyNameFormField?.value.validationMessage),
    formatType: yup
      .string<CompanyFormatTypeDto>()
      .required(
        language === 'fr'
          ? (fields.companyTypeFRList?.value?.requiredMessage ?? fields.companyTypeFormField?.value?.requiredMessage)
          : fields.companyTypeFormField?.value?.requiredMessage,
      ),
    companyNumber: yup.string().when('showCompanyNumber', {
      is: false,
      then: schema =>
        schema
          .required(fields.companyNumberFormField?.value.requiredMessage ?? 'Dit is verplicht [not in sitecore]')
          .test(
            'pattern',
            fields.companyNumberFormField?.value.validationMessage ?? 'Niet het juiste patroon[not in sitecore]',
            function (value) {
              return isValidCompanyNumber(value);
            },
          ),
    }),
  });

  type FormValues = yup.InferType<typeof editCompanyDataSchema>;
  const resolver = typedYupResolver<FormValues>(editCompanyDataSchema);

  const {
    control,
    handleSubmit,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver,
    defaultValues: { showCompanyNumber: false },
  });

  const { data, isLoading, error } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/contact/details`],
    session,
  );

  const contactData = unwrapData(data);

  const companyData = contactData?.companyData;
  const hasExistingCompanyNumber = !!companyData?.companyNumber;
  const isCompanyNumberPending = companyData?.vatType === 'CompanyNumberRequested';
  const showCompanyNumber = watch('showCompanyNumber');
  const companyTypeOptions = fields.companyTypeFRList.value.options.concat(fields.companyTypeFormField.value.options);
  const companyTypeLabel =
    companyTypeOptions.find(option => option.value === companyData?.formatType)?.label ?? companyData?.formatType;

  useEffect(() => {
    if (!companyData) {
      setValue('showCompanyNumber', false);
      setValue('companyNumber', '');
      return;
    }

    setValue('name', companyData.name ?? '');
    setValue('formatType', companyData.formatType ?? '');
    setValue('showCompanyNumber', isCompanyNumberPending);
    setValue('companyNumber', companyData.companyNumber ?? '');
  }, [companyData, isCompanyNumberPending, setValue]);

  if (contactData?.customerType !== 'SOHO') {
    return null;
  }

  const submitForm: SubmitHandler<FormValues> = async values => {
    try {
      setIsLoadingSubmit(true);
      const { response, error } = await putEnecoBeXapiSiteApiV1AccountsByAccountNumberCompanyDetails({
        path: {
          accountNumber: selectedAccount?.crmAccountNumber,
        },
        body: {
          name: values.name,
          formatType: values.formatType,
          vatType: getValues('showCompanyNumber') ? 'CompanyNumberRequested' : 'VatRequired',
          companyNumber: values.companyNumber || '',
        },
      });
      if (response.ok) {
        trackEventBE('company_data_submit', { status: 'success' });
        mutate(`/accounts/${selectedAccount.crmAccountNumber}/company/details`);
        redirectAndNotify({
          route: fields.actionLinksSaveCompanyDataLink.value.href,
          title: fields.successNotification?.value.title,
          text: fields.successNotification?.value.content ?? 'Je bedrijf is aangepast [not in sitecore]',
          variant: 'success',
        });
      } else {
        const validationErrors = unwrapValidationError(error);
        throw new Error(validationErrors.join(', '));
      }
    } catch {
      setIsLoadingSubmit(false);
      setNotification({
        notificationOptions: {
          variant: 'error',
          title: fields.failureNotification?.value.title,
          text: fields.failureNotification?.value.content,
          headingLevel: 'h3',
        },
        shouldScroll: true,
      });
      trackEventBE('company_data_submit', { status: 'failed', message: 'unexpected error occurred' });
    }
  };
  if (isCurrentAccountReader) {
    return;
  }

  if (!isLoading && !!error && !data) {
    return <DataErrorNotification />;
  }

  return (
    <Bucket title={fields.title.value}>
      <Form onSubmit={handleSubmit(submitForm)}>
        <Bucket.Content>
          <ReadFormField
            label={fields.companyNameFormField.value.label}
            value={companyData?.name}
            isLoading={isLoading}
          />
          <ReadFormField
            label={fields.companyTypeFormField.value.label}
            value={companyTypeLabel}
            isLoading={isLoading}
          />
          {hasExistingCompanyNumber ? (
            <ReadFormField
              label={fields.companyNumberFormField?.value.label}
              value={companyData?.companyNumber}
              isLoading={isLoading}
            />
          ) : (
            !showCompanyNumber && (
              <Controller
                control={control}
                name="companyNumber"
                render={({ field: { onChange, value, name } }) => (
                  <CompanyNumberInput
                    label={fields.companyNumberFormField?.value.label}
                    name={name}
                    placeholder="XXXX.XXX.XXX"
                    error={errors.companyNumber?.message}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            )
          )}
          {!hasExistingCompanyNumber && (
            <Checkbox
              {...register('showCompanyNumber')}
              label={fields.companyNumberNotKnownYetText?.value || 'Ondernemingsnummer in aanvraag [not in sitecore]'}
            />
          )}
          <Bucket.Actions>
            {!hasExistingCompanyNumber && (
              <Button type="submit" isLoading={isLoadingSubmit}>
                {fields.actionLinksSaveCompanyDataLink?.value.text}
              </Button>
            )}
            <Button onClick={() => push(fields.actionLinksCancelCompanyDataLink.value.href)} action={'secondary'}>
              {fields.actionLinksCancelCompanyDataLink?.value.text}
            </Button>
          </Bucket.Actions>
        </Bucket.Content>
      </Form>
    </Bucket>
  );
};

export default EditCompanyData;
